import { apiClient } from '../../api/client';
import type { components } from '../../api/generated/schema';
import type { SignupRequest } from '../../domain/account';
import { DEFAULT_LIMITS } from '../../domain/invitations';
import type { GroupLinkLimits, MatchedContact } from '../../domain/invitations';
import { mapped } from '../errors';
import type { InvitationsRepository } from './InvitationsRepository';
import {
  toAcceptResult,
  toCreatedInvitation,
  toGroupLinkBody,
  toInvitation,
  toInvitationLink,
  toInvitePreview,
  toInviteSignup,
  toReceivedInvitation,
} from './mapping';

type S = components['schemas'];

const path = (id: string) => `/api/v1/invitations/${encodeURIComponent(id)}/`;
const receivedPath = (id: string) => `/api/v1/invitations/received/${encodeURIComponent(id)}/`;

/** Tokens go only in request bodies — never a path, a query or a log line. */
export const httpInvitationsRepository: InvitationsRepository = {
  createGroupLink: (
    offeredCollectionIds: string[],
    label: string,
    limits: GroupLinkLimits = DEFAULT_LIMITS,
  ) =>
    mapped(async () =>
      toCreatedInvitation(
        await apiClient.invitationCreate({
          kind: 'group',
          offered_collection_ids: offeredCollectionIds,
          label,
          ...toGroupLinkBody(limits),
        }),
      ),
    ),
  createPersonalInvite: (
    inviteeId: string,
    offeredCollectionIds: string[],
    matchedContact: MatchedContact,
  ) =>
    mapped(async () =>
      toCreatedInvitation(
        await apiClient.invitationCreate({
          kind: 'personal',
          invitee_id: inviteeId,
          offered_collection_ids: offeredCollectionIds,
          label: '',
          matched_contact: matchedContact,
        }),
      ),
    ),
  list: () =>
    mapped(async () =>
      (await apiClient.get<S['Invitation'][]>('/api/v1/invitations/')).map(toInvitation),
    ),
  received: () =>
    mapped(async () =>
      (await apiClient.get<S['ReceivedInvitation'][]>('/api/v1/invitations/received/')).map(
        toReceivedInvitation,
      ),
    ),
  receivedInvitation: (invitationId: string) =>
    mapped(async () =>
      toReceivedInvitation(
        await apiClient.get<S['ReceivedInvitation']>(receivedPath(invitationId)),
      ),
    ),
  acceptReceived: (invitationId: string, joinedOfferIds: string[]) =>
    mapped(async () => {
      const body: S['ReceivedInviteAcceptRequest'] = { joined_offer_ids: joinedOfferIds };
      return toAcceptResult(
        await apiClient.send<S['InviteAcceptResult']>(
          'POST',
          `${receivedPath(invitationId)}accept/`,
          body,
        ),
      );
    }),
  declineReceived: (invitationId: string) =>
    mapped(async () => {
      await apiClient.send('POST', `${receivedPath(invitationId)}decline/`);
    }),
  get: (id: string) =>
    mapped(async () => toInvitation(await apiClient.get<S['Invitation']>(path(id)))),
  newLink: (invitationId: string) =>
    mapped(async () =>
      toInvitationLink(
        await apiClient.send<S['InvitationLinkCreated']>('POST', `${path(invitationId)}links/`),
      ),
    ),
  revoke: (invitationId: string) =>
    mapped(async () => {
      await apiClient.send('POST', `${path(invitationId)}revoke/`);
    }),
  revokeLink: (linkId: string) =>
    mapped(async () => {
      await apiClient.send(
        'POST',
        `/api/v1/invitation-links/${encodeURIComponent(linkId)}/revoke/`,
      );
    }),
  preview: (token: string) =>
    mapped(async () => toInvitePreview(await apiClient.invitePreview(token))),
  accept: (token: string, joinedOfferIds: string[]) =>
    mapped(async () => toAcceptResult(await apiClient.inviteAccept(token, joinedOfferIds))),
  decline: (token: string) =>
    mapped(async () => {
      await apiClient.send('POST', '/api/v1/invites/decline/', { token });
    }),
  signUpThroughLink: (token: string, request: SignupRequest) =>
    mapped(async () => {
      await apiClient.send('POST', '/api/v1/invites/signup/', toInviteSignup(token, request));
    }),
};
